import {
  Divider,
  Hidden,
  useMediaQuery,
  useTheme,
  Box,
  Typography,
} from '@mui/material';
import {
  GenUnitCard,
  CollapsibleTable,
  WorkOrderStepper,
  GenSnackBar,
} from '@pinnacleautomotive/clarity-components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import carPlaceholder from '../../../assets/carPlaceholder.png';
import Empty from '../../../assets/emptyMyOrders.png';
import ServicesDetails from '../../../components/ServicesDetails';
import { hasDealerShipManagerRole } from '../../../helpers/users';
import { getMyOrders } from '../../../redux/actions/orders/myOrdersActions';
import {
  cancelStepChangeStatusAllOrders,
  hideCancelConfirmation,
} from '../../../redux/actions/workQueue/allOrdersActions';
import { useGetVehiclePhotoCountMutation } from '../../../redux/rtk/vendors.services';
import {
  selectMyOrders,
  selectHasMore,
  selectIsLoading,
  selectCurrentPage,
} from '../../../redux/selectors/orders/myOrdersSelectors';
import { selectCurrentUserRole } from '../../../redux/selectors/userDataSelectors';
import {
  isChangeStepStatusApiLoading,
  selectShowCancelError,
  selectShowCancelSuccesful,
} from '../../../redux/selectors/workQueue/allOrdersSelectors';
import { CommonColumns } from '../commons';
import EmptyState from '../components/EmptyState';

const MyOrders = ({ searchQuery, personId, companyId }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation('orders');
  const orders = useSelector(selectMyOrders);
  const theme = useTheme();
  const mediumScreen = useMediaQuery(theme.breakpoints.up('md'));

  const hasMore = useSelector(selectHasMore);
  const loading = useSelector(selectIsLoading);
  const currentPage = useSelector(selectCurrentPage);
  const userRoles = useSelector(selectCurrentUserRole);
  const showCancelSuccesSnackBar = useSelector(selectShowCancelSuccesful);
  const showCancelErrorSnackBar = useSelector(selectShowCancelError);
  const isChangeStepOrderApiLoading = useSelector(isChangeStepStatusApiLoading);
  const [photoCount, setPhotoCount] = useState(null);
  const [putVehicle, { isLoading: isLoadingVehicle }] =
    useGetVehiclePhotoCountMutation();

  useEffect(() => {
    dispatch(getMyOrders({ personId, companyId, pageNumber: 1, searchQuery }));
    dispatch(hideCancelConfirmation());
  }, [personId, companyId, dispatch, searchQuery]);

  const handleCancelStatus = ({ vendorOrderId, cancellingNote }) => {
    dispatch(
      cancelStepChangeStatusAllOrders({ vendorOrderId, cancellingNote })
    );
  };

  const loadMoreData = () => {
    dispatch(
      getMyOrders({
        personId,
        companyId,
        pageNumber: currentPage + 1,
        searchQuery,
      })
    );
  };

  const dispatchHideCancelConfirmation = () => {
    dispatch(hideCancelConfirmation());
  };

  const columns = CommonColumns(t, mediumScreen, []);

  const handleApi = async row => {
    if (!row?.vehicle?.vin) return;

    const body = {
      orderId: row?.orderId,
      dealerId: row?.dealer?.companyId,
    };
    try {
      const data = await putVehicle({
        body,
        vinOrStockNumber: row?.vehicle?.vin,
      }).unwrap();
      const photoCountValue = row?.vehicle?.showPhotoCount
        ? data?.photosCount || 0
        : null;
      setPhotoCount(prev => ({ ...prev, [row.orderId]: photoCountValue }));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Error in fetching photo count', err);
      setPhotoCount(prev => ({ ...prev, [row.orderId]: null }));
    }
  };

  const serviceDetails = data => {
    return (
      <ServicesDetails
        data={data}
        isLoadingVehicle={isLoadingVehicle}
        photoCount={photoCount}
        showPhotoCount={true}
      />
    );
  };
  return (
    <>
      <GenSnackBar
        title={t('cancelConfirmation')}
        open={showCancelSuccesSnackBar}
        onClose={() => dispatchHideCancelConfirmation()}
      />
      <GenSnackBar
        title={t('cancelError')}
        open={showCancelErrorSnackBar}
        type="error"
        onClose={() => dispatchHideCancelConfirmation()}
      />
      <CollapsibleTable
        columns={columns}
        rows={orders}
        hasNextPage={hasMore}
        loading={loading}
        onLoadMore={() => loadMoreData()}
        onClick={workOrder =>
          history.push({
            pathname: `/orders/${workOrder.vendorOrderId}`,
          })
        }
        handleApi={handleApi}
        collpasibleGetter={data => (
          <Box display="grid">
            <Box p={2} order={{ md: 2 }}>
              <Typography variant="subtitle2" color="textPrimary">
                {t('processStep.title')}
              </Typography>
            </Box>
            <Box p={2} mb={1} order={{ md: 3 }}>
              <WorkOrderStepper
                isVendor={false}
                isManager={hasDealerShipManagerRole([userRoles.roleName])}
                vendorOrderId={data.vendorOrderId}
                steps={data.workFlowHistory}
                statusCancel={handleCancelStatus}
                isApiLoading={isChangeStepOrderApiLoading}
                detail={true}
              />
            </Box>
            <Box pt={2}>
              <Hidden mdUp>
                <Divider />
              </Hidden>
              <GenUnitCard
                order={data}
                image={carPlaceholder}
                showStockAndWoNumber={false}
              >
                {serviceDetails(data)}
              </GenUnitCard>
            </Box>
          </Box>
        )}
      />
      <EmptyState
        show={!loading && !orders.length}
        img={Empty}
        title={t('global:noWO')}
        subtitle={t('global:noWOMyOrders')}
      />
    </>
  );
};

export default MyOrders;
